<template>
    <section :id="id" class="s-slice s-intro" :class="{ ...className, ...basicClassName }">
        <div class="a-container">
            <a-label v-if="l10n.label" v-bind="l10n.label" :theme="theme">
                {{ l10n.label.text }}
            </a-label>
            <component :is="titleTag" v-if="l10n.title" class="title">
                {{ l10n.title }}
            </component>
            <div class="wrapper">
                <div v-if="l10n.lead || l10n.bodyText || l10n.link" class="main-content">
                    <a-dangerous-html
                        v-if="l10n.lead"
                        class="lead"
                        :content="l10n.lead"
                        :tag="'p'"
                    />
                    <a-dangerous-html v-if="l10n.bodyText" class="body-text" :content="l10n.bodyText" />
                    <a-link
                        v-if="l10n.link"
                        class="link"
                        size="title"
                        v-bind="l10n.link"
                        :type="linkType"
                        :glyph="l10n.link.glyph || 'arrow'"
                    />
                </div>
                <div v-if="withAdditionalInfo" class="additional-info">
                    <div v-if="l10n.downloadFileArr?.length" class="download-files">
                        <ul v-if="l10n.downloadFileArr?.length > 1" class="download-file-list">
                            <li v-for="item in l10n.downloadFileArr" :key="item.link" class="download-file-item">
                                <a-link
                                    size="lead"
                                    v-bind="item"
                                    :to="item.link"
                                    :glyph="item.glyph || 'download-arrow'"
                                    :glyph-position="item.glyphPosition || 'left'"
                                    :text="item.title"
                                    :desc="item.desc"
                                    :type="linkType"
                                />
                            </li>
                        </ul>
                        <a-link
                            v-else
                            v-bind="l10n.downloadFileArr[0]"
                            :to="l10n.downloadFileArr[0].link"
                            :glyph="l10n.downloadFileArr[0]?.glyph || 'download-arrow'"
                            :glyph-position="l10n.downloadFileArr[0]?.glyphPosition || 'left'"
                            :text="l10n.downloadFileArr[0].title"
                            :desc="l10n.downloadFileArr[0].desc"
                            :type="linkType"
                        />
                    </div>
                    <blockquote v-if="l10n.quote" class="quote">
                        <div class="quote-text">
                            {{ l10n.quote.text }}
                        </div>
                        <cite class="quote-cite">
                            <div class="quote-img-container">
                                <a-picture class="quote-img" :link="l10n.quote.image" :alt="l10n.quote.name" />
                            </div>
                            <div class="quote-cite-text">
                                <div class="quote-name">{{ l10n.quote.name }}</div>
                                <div class="quote-position">{{ l10n.quote.position }}</div>
                            </div>
                        </cite>
                    </blockquote>
                    <div v-if="l10n.fact" class="fact">
                        <a-icon
                            v-if="l10n.fact.icon"
                            class="fact-icon"
                            :number="l10n.fact.icon"
                            :icon-alt="l10n.fact.title"
                            :icon-variant="l10n.fact.iconVariant"
                        />
                        <div class="fact-title">
                            {{ l10n.fact.title }}
                        </div>
                        <a-dangerous-html class="fact-text" :content="l10n.fact.text" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import AIcon from '@core/components/icon/icon.vue';
import ALabel from '@core/components/label/label.vue';
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import contentMixin from '@core/mixins/content.js';
import l10n from '@core/mixins/l10n.js';
import styleMixin from '@core/mixins/style.js';

const BG_STYLES = ['white', 'navy', 'grey', 'top-gradient', 'navy-gradient'];

export default {
    name: 'SIntro',

    components: {
        ALink,
        ADangerousHtml,
        APicture,
        AIcon,
        ALabel,
    },

    mixins: [l10n, contentMixin, styleMixin],

    props: {
        label: {
            type: Object,
            default: null,
        },
        titleTag: {
            type: String,
            default: 'div',
        },
        hugeTitle: {
            type: Boolean,
            default: false,
        },
        link: {
            type: [Object, Boolean],
            default: false,
        },
        fact: {
            type: [Object, Boolean],
            default: false,
        },
        quote: {
            type: Object,
            required: false,
            default: undefined,
        },
        downloadFileArr: {
            type: Array,
            required: false,
            default: undefined,
        },
        background: {
            type: String,
            default: 'white',
            validator: (val) => BG_STYLES.includes(val),
        },
    },

    computed: {
        theme() {
            return ['navy', 'navy-gradient'].includes(this.background) ? 'dark' : 'light';
        },

        linkType() {
            return this.theme === 'dark' ? 'light' : 'regular';
        },

        withAdditionalInfo() {
            return this.l10n.fact || this.l10n.quote || this.l10n.downloadFileArr;
        },

        className() {
            return {
                'with-additional-info': this.withAdditionalInfo,
                'with-huge-title': this.l10n.hugeTitle,
            };
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-intro {
    @mixin basic-slice-paddings;
    color: var(--av-nav-primary);

    &:deep(.a-picture__img) {
        width: 100%;
    }

    &:deep(.a-link__content) {
        font-weight: 700;
    }

    &.with-huge-title {
        .title {
            @mixin display-accent;

            @media (--viewport-mobile-wide) {
                @mixin hero-accent;
            }

            @media (--viewport-desktop) {
                @mixin large-accent;
            }
        }
    }

    &:not(.with-additional-info) {
        @media (--viewport-desktop) {
            .wrapper {
                grid-template-columns: 1fr;
                width: 66.66%;
            }

            .title,
            .lead,
            .body-text {
                width: 100%;
            }
        }
    }

    &.s-slice_background_navy {
        background: var(--av-nav-primary);
    }

    &.s-slice_background_grey {
        background: var(--av-solid-brand-lightest);
    }

    &.s-slice_background_top-gradient {
        background: linear-gradient(180deg, rgba(38, 104, 197, 0.05) 0%, rgba(38, 104, 197, 0.00) 100%);
    }

    &.s-slice_background_navy-gradient {
        background: linear-gradient(302deg, #2668c5 6.31%, #00204d 86.97%);
    }

    &.s-slice_background_navy,
    &.s-slice_background_navy-gradient {
        color: var(--av-inversed-primary);

        .quote-position,
        .quote-text,
        .fact-text {
            color: var(--av-inversed-primary);
        }

        .download-file-item:deep(.a-link__desc) {
            color: var(--av-inversed-light);
        }
    }
}

.title {
    @mixin hero-accent;
}

.lead {
    @mixin title;
    margin-top: 24px;
}

.body-text {
    @mixin paragraph;
    margin-top: 24px;
}

.link {
    margin-top: 40px;
}

.download-file-list {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 24px;
}

.download-file-item {
    &:deep(.a-link__desc) {
        padding-inline-start: 24px;
    }
}

.additional-info {
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.fact-title {
    @mixin title-accent;

    margin-bottom: 8px;
}

.fact-text {
    @mixin paragraph;
    color: var(--av-fixed-secondary);
}

.fact-icon {
    margin-bottom: 16px;
    display: block;
    width: 48px;
    height: 48px;
}

.quote-cite-text {
    width: 100%;
}

.quote-text {
    margin-bottom: 24px;
    color: var(--av-fixed-secondary);
}

.quote-cite {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
}

.quote-name {
    @mixin paragraph-accent;
}

.quote-position {
    @mixin body;
    color: var(--av-fixed-secondary);
}

.quote-img-container {
    width: 64px;
    height: 64px;
    flex: 0 0 64px;
    border-radius: 32px;
    border: 1px solid var(--av-brand-secondary-light);
    background: var(--av-inversed-primary);
    overflow: hidden;
}

@media (--viewport-mobile-wide) {
    .download-file-list {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 24px;
    }

    .download-file-item {
        padding-inline-end: 16px;
    }

    .info-title {
        max-width: none;
    }

    .quote-cite {
        flex-direction: row;
        align-items: center;
    }

    .quote-position {
        padding-inline-end: 20px;
        line-height: 1.8;
    }
}

@media (--viewport-desktop) {
    .wrapper {
        display: grid;
        grid-template-columns: 2fr 1fr;
        grid-column-gap: 5%;
    }

    .info-title {
        max-width: 86%;
    }

    .quote-cite {
        flex-direction: row;
    }

    .additional-info {
        margin-top: 24px;
        padding-inline-start: 24px;
        border-inline-start: 1px solid var(--av-brand-secondary-light);
        gap: 32px;
    }

    .download-file-list {
        display: flex;
        flex-direction: column;
    }

    .download-file-item {
        width: 100%;
        padding-inline-end: 16px;
    }
}

@media (--viewport-desktop-wide) {
    .title,
    .lead,
    .body-text {
        width: 85%;
    }
}
</style>
